import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_CONTACT_REQUIREMENTS,
  normalizeContactRequirements,
  type ContactRequirements,
} from "@/lib/profileCompleteness";

// Per-tenant listing quality-control config, edited in the admin Quality control
// panel and read by the Postgres recompute function (which materializes each
// listing's `quality_visible` flag). The table is admin-only by RLS, so this
// hook only resolves for admins.

/** The 12 governed rules, in display order. */
export const QUALITY_RULE_KEYS = [
  "minPhotos",
  "descriptionExists",
  "descriptionNoNumbers",
  "descriptionCompliant",
  "priceSet",
  "gpsExifDistance",
  "walkBeachDiff",
  "ownerHasAvatar",
  "ownerHasBio",
  "ownerResponseRate",
  "photosLabeled",
  "essentialsAnswered",
] as const;

export type QualityRuleKey = (typeof QUALITY_RULE_KEYS)[number];

export interface QualityRuleConfig {
  /** Rule participates in evaluation (shown to owner as a check). */
  enabled: boolean;
  /** Failing this rule hides the listing. Non-deal-breakers are soft warnings. */
  dealBreaker: boolean;
  // Rule-specific thresholds (only the relevant one is used per rule).
  min?: number; // minPhotos
  maxMeters?: number; // gpsExifDistance
  maxMinutes?: number; // walkBeachDiff
  minLen?: number; // ownerHasBio
  minPct?: number; // ownerResponseRate
  minSample?: number; // ownerResponseRate
  graceDays?: number; // ownerResponseRate: requests count once this many days old
  minAnswered?: number; // essentialsAnswered (of the 10 essentials in src/lib/amenities.ts)
}

export type QualityRules = Record<QualityRuleKey, QualityRuleConfig>;

export interface QualityConfig {
  tenant_id: string;
  enabled: boolean;
  rules: QualityRules;
  description_standards: string;
  /** Which profile fields a renter must fill before contacting an owner.
   *  Independent of `enabled` (the listing-gate master switch). */
  renter_contact_fields: ContactRequirements;
}

// Mirrors the column / rule defaults in the quality_control migration, so a
// tenant with no row yet (or a freshly added city) edits sensible starting
// values and every rule key is always present.
export const QUALITY_DEFAULTS: Omit<QualityConfig, "tenant_id"> = {
  enabled: false,
  description_standards: "",
  renter_contact_fields: { ...DEFAULT_CONTACT_REQUIREMENTS },
  rules: {
    minPhotos: { enabled: false, dealBreaker: false, min: 4 },
    descriptionExists: { enabled: false, dealBreaker: false },
    descriptionNoNumbers: { enabled: false, dealBreaker: false },
    descriptionCompliant: { enabled: false, dealBreaker: false },
    priceSet: { enabled: false, dealBreaker: false },
    gpsExifDistance: { enabled: false, dealBreaker: false, maxMeters: 150 },
    walkBeachDiff: { enabled: false, dealBreaker: false, maxMinutes: 10 },
    ownerHasAvatar: { enabled: false, dealBreaker: false },
    ownerHasBio: { enabled: false, dealBreaker: false, minLen: 40 },
    ownerResponseRate: { enabled: false, dealBreaker: false, minPct: 50, minSample: 3, graceDays: 3 },
    photosLabeled: { enabled: false, dealBreaker: false },
    essentialsAnswered: { enabled: false, dealBreaker: false, minAnswered: 10 },
  },
};

/** Fill in any rule keys/thresholds missing from a stored config with defaults,
 *  so older rows (saved before a rule existed) always present the full set. */
export const normalizeRules = (raw: unknown): QualityRules => {
  const stored = (raw && typeof raw === "object" ? raw : {}) as Record<string, Partial<QualityRuleConfig>>;
  const out = {} as QualityRules;
  for (const key of QUALITY_RULE_KEYS) {
    out[key] = { ...QUALITY_DEFAULTS.rules[key], ...(stored[key] ?? {}) };
  }
  return out;
};

export const qualityConfigKey = (tenantId: string) => ["quality_config", tenantId] as const;

export function useQualityConfig(tenantId: string) {
  return useQuery({
    queryKey: qualityConfigKey(tenantId),
    queryFn: async (): Promise<QualityConfig | null> => {
      const { data, error } = await supabase
        .from("quality_config")
        .select("tenant_id, enabled, rules, description_standards, renter_contact_fields")
        .eq("tenant_id", tenantId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        tenant_id: data.tenant_id,
        enabled: data.enabled,
        rules: normalizeRules(data.rules),
        description_standards: data.description_standards ?? "",
        renter_contact_fields: normalizeContactRequirements(data.renter_contact_fields),
      };
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export const contactRequirementsKey = (tenantId: string) =>
  ["renter_contact_requirements", tenantId] as const;

/**
 * Public read of the tenant's renter-contact requirements (which fields a
 * renter must fill before messaging a landlord). Backed by the
 * get_renter_contact_requirements SECURITY DEFINER RPC so it works for anon
 * and renters even though quality_config itself is admin-only. Falls back to
 * the standard bar (all fields required) if the RPC is missing or errors.
 */
export function useContactRequirements(tenantId: string) {
  return useQuery({
    queryKey: contactRequirementsKey(tenantId),
    queryFn: async (): Promise<ContactRequirements> => {
      const { data, error } = await supabase.rpc("get_renter_contact_requirements", {
        _tenant_id: tenantId,
      });
      if (error) return { ...DEFAULT_CONTACT_REQUIREMENTS };
      return normalizeContactRequirements(data);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
